import React from 'react';
import { motion } from 'framer-motion';
import { Scanline } from './Scanline';
import { FileTextIcon, TargetIcon, UsersIcon, PlugZapIcon, SearchIcon, QuoteIcon } from 'lucide-react';
const extractionItems = ['Borrower goals', 'Objections', 'Income mentions', 'Credit details', 'Property info', 'Requested documents', 'Follow-up items', 'Important quotes'];
const dialers = ['RingCentral', 'Dialpad', 'Five9', 'Zoom', 'Teams', 'Any dialer'];
const features = [{
  icon: FileTextIcon,
  code: 'FN.03',
  title: 'AI mortgage-specific notes',
  description: 'Not generic meeting summaries. Notes structured around the loan conversation — written for loan officers, by mortgage call intelligence.'
}, {
  icon: UsersIcon,
  code: 'FN.04',
  title: 'Borrower profiles & timelines',
  description: 'Every call is filed under the right borrower with a chronological timeline, so you see the full relationship at a glance.'
}, {
  icon: SearchIcon,
  code: 'FN.05',
  title: 'Instant search',
  description: 'Search across every transcript and note in seconds. Find the call where the rate was discussed without scrubbing audio.'
}, {
  icon: QuoteIcon,
  code: 'FN.06',
  title: 'Source-backed answers',
  description: 'Every answer links back to the source call and transcript excerpt, so you can trust — and verify — what you read.'
}];
const cardBase = 'group relative rounded-2xl border border-slate-200 bg-white p-6 shadow-sm transition-all duration-300 hover:border-teal-600/40 hover:shadow-[0_8px_40px_-12px_rgba(13,148,136,0.25)]';
export function Features() {
  return <section id="features" className="relative scroll-mt-16 border-t border-slate-100 py-20 lg:py-28" data-id="element-110">
      <Scanline data-id="element-111" />
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8" data-id="element-112">
        <motion.div initial={{
        opacity: 0,
        y: 24
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-80px'
      }} transition={{
        duration: 0.6
      }} className="mx-auto max-w-2xl text-center" data-id="element-113">
          <p className="font-mono-brand text-xs uppercase tracking-[0.2em] text-teal-600" data-id="element-114">
            // features
          </p>
          <h2 className="font-display mt-4 text-3xl font-bold tracking-tight text-ink sm:text-4xl lg:text-5xl" data-id="element-115">
            Mortgage call intelligence, built around the borrower
          </h2>
        </motion.div>

        <div className="mt-14 grid grid-cols-1 gap-6 lg:grid-cols-3" data-id="element-116">
          <motion.div initial={{
          opacity: 0,
          y: 24
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-60px'
        }} transition={{
          duration: 0.5
        }} className={`${cardBase} lg:col-span-2`} data-id="element-117">
            <span className="font-mono-brand absolute right-5 top-5 text-[10px] tracking-widest text-slate-600" data-id="element-118">
              FN.01
            </span>
            <span className="flex h-11 w-11 items-center justify-center rounded-lg border border-teal-600/30 bg-teal-50" data-id="element-119">
              <TargetIcon className="h-5 w-5 text-teal-600" aria-hidden="true" data-id="element-120" />
            </span>
            <h3 className="font-display mt-4 text-lg font-bold text-slate-900" data-id="element-121">
              Extracts what matters on every call
            </h3>
            <p className="mt-2 text-sm leading-6 text-slate-600" data-id="element-122">
              The AI listens for the details that move a loan forward and pulls
              them into structured notes automatically.
            </p>
            <ul className="mt-5 flex flex-wrap gap-2" data-id="element-123">
              {extractionItems.map(item => <li key={item} className="font-mono-brand rounded border border-teal-600/25 bg-teal-50 px-3 py-1.5 text-xs text-teal-700" data-id="element-124">
                  {item}
                </li>)}
            </ul>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: 24
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-60px'
        }} transition={{
          duration: 0.5,
          delay: 0.1
        }} className={cardBase} data-id="element-125">
            <span className="font-mono-brand absolute right-5 top-5 text-[10px] tracking-widest text-slate-600" data-id="element-126">
              FN.02
            </span>
            <span className="flex h-11 w-11 items-center justify-center rounded-lg border border-teal-600/30 bg-teal-50" data-id="element-127">
              <PlugZapIcon className="h-5 w-5 text-teal-600" aria-hidden="true" data-id="element-128" />
            </span>
            <h3 className="font-display mt-4 text-lg font-bold text-slate-900" data-id="element-129">
              Works with any dialer
            </h3>
            <p className="mt-2 text-sm leading-6 text-slate-600" data-id="element-130">
              No integrations, no IT tickets. If the call audio plays through
              your computer, it's covered.
            </p>
            <ul className="mt-5 flex flex-wrap gap-2" data-id="element-131">
              {dialers.map(d => <li key={d} className="font-mono-brand rounded border border-slate-200 bg-slate-50 px-3 py-1.5 text-xs text-slate-600" data-id="element-132">
                  {d}
                </li>)}
            </ul>
          </motion.div>

          {features.map((feature, i) => <motion.div key={feature.title} initial={{
          opacity: 0,
          y: 24
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-60px'
        }} transition={{
          duration: 0.5,
          delay: 0.1 + i * 0.08
        }} className={cardBase} data-id="element-133">
              <span className="font-mono-brand absolute right-5 top-5 text-[10px] tracking-widest text-slate-600" data-id="element-134">
                {feature.code}
              </span>
              <span className="flex h-11 w-11 items-center justify-center rounded-lg border border-teal-600/30 bg-teal-50" data-id="element-135">
                <feature.icon className="h-5 w-5 text-teal-600" aria-hidden="true" data-id="element-136" />
              </span>
              <h3 className="font-display mt-4 text-lg font-bold text-slate-900" data-id="element-137">
                {feature.title}
              </h3>
              <p className="mt-2 text-sm leading-6 text-slate-600" data-id="element-138">
                {feature.description}
              </p>
            </motion.div>)}
        </div>
      </div>
    </section>;
}